"use client";

import React, { useState } from "react";
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  X,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import {
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
} from "react-icons/md";
import { usePolicyCategories } from "../../context/PolicyCategoryContext";
import { useAuth } from "../../context/AuthContext";

// Reusable input class so every field shares the same lavender focus style
const inputClass =
  "w-full border border-gray-300 rounded-lg px-3 py-2 outline-none transition-colors duration-150 focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40";

export default function PolicyCategoryManagement() {
  const {
  categories,
  loading,
  createCategory,
  updateCategory,
  deleteCategory,
  updateStatus,
  setCategories,
} = usePolicyCategories();
const { canDo } = useAuth();

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");

  const [currentCategory, setCurrentCategory] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const [submitting, setSubmitting] = useState(false);
  const [statusLoading, setStatusLoading] = useState(null);

  const statusColors = {
    active: "bg-green-100 text-green-600",
    inactive: "bg-red-100 text-red-600",
  };

  const filteredCategories = categories.filter((category) => {
    const matchesStatus =
      filterStatus === "All" || category.status === filterStatus;

    const matchesSearch = category.category_name
      ?.toLowerCase()
      .includes(searchQuery.toLowerCase());

    return matchesStatus && matchesSearch;
  });

  const totalPages = Math.ceil(
    filteredCategories.length / itemsPerPage
  );

  const paginatedCategories = filteredCategories.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

 const handleSubmit = async (e) => {
  e.preventDefault();

  const form = e.target;

  const category_name = form.category_name.value.trim();
  const status = form.status.value;

  if (!category_name) {
    return alert("Please enter category name");
  }

  const userData = JSON.parse(localStorage.getItem("user"));

  try {
    setSubmitting(true);

    let response;

    if (currentCategory) {
      response = await updateCategory(currentCategory.id, {
        category_name,
        status,
        update_audit_id: userData?.id,
      });
    } else {
      response = await createCategory({
        category_name,
        status,
        create_audit_id: userData?.id,
      });
    }

    if (response.success) {
      setShowModal(false);
      setCurrentCategory(null);
    } else {
      alert(response.message);
    }
  } catch (error) {
    console.error(error);
    alert("Something went wrong");
  } finally {
    setSubmitting(false);
  }
};

const handleStatusToggle = async (category) => {
  const userData = JSON.parse(localStorage.getItem("user"));

  const newStatus =
    category.status === "active" ? "inactive" : "active";

  // optimistic update FIRST
  setCategories((prev) =>
    prev.map((item) =>
      item.id === category.id
        ? { ...item, status: newStatus }
        : item
    )
  );

  const response = await updateStatus(category.id, {
    status: newStatus,
    update_audit_id: userData?.id,
  });

  // rollback if failed
  if (!response.success) {
    setCategories((prev) =>
      prev.map((item) =>
        item.id === category.id
          ? { ...item, status: category.status }
          : item
      )
    );

    alert(response.message || "Failed to update status");
  }
};

 const handleDelete = async () => {
  const userData = JSON.parse(localStorage.getItem("user"));

  const response = await deleteCategory(
    deleteId,
    userData?.id
  );

  if (response.success) {
    setDeleteId(null);
    setShowDeleteModal(false);
  } else {
    alert(response.message);
  }
};

  return (
    <>
      <div className="bg-sky-50">
        <div className="px-9 py-5 max-md:p-4">

          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-5">
            Policy Categories
          </h1>

          {/* Search + Add */}
          <div className="flex justify-between items-center flex-wrap gap-3 mb-6 bg-white rounded-xl px-4 py-3 shadow-sm">

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center flex-wrap gap-3">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center border border-gray-300 rounded-lg bg-white w-full sm:w-86 md:w-[300px] lg:w-[350px] overflow-hidden transition-colors duration-150 focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">

                <div className="flex items-center flex-1 px-3 py-2">
                  <Search className="text-gray-400 h-3 w-3 mr-2" />

                  <input
                    type="text"
                    placeholder="Search category..."
                    value={searchQuery}
                    onChange={(e) =>
                      setSearchQuery(e.target.value)
                    }
                    className="flex-1 outline-none text-gray-700 bg-transparent"
                  />
                </div>

                <select
                  value={filterStatus}
                  onChange={(e) =>
                    setFilterStatus(e.target.value)
                  }
                  className="border-t sm:border-t-0 sm:border-l border-gray-300 px-3 py-2 outline-none bg-white"
                >
                  <option>All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {(searchQuery || filterStatus !== "All") && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    setFilterStatus("All");
                  }}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
                >
                  <X size={14} />
                  Clear Filters
                </button>
              )}
            </div>

            {canDo("PolicyCategoryManagement", "Add") && (
              <button
                onClick={() => {
                  setCurrentCategory(null);
                  setShowModal(true);
                }}
                className="flex items-center text-white px-4 py-2 rounded-lg"
                style={{
                  background:
                    "linear-gradient(135deg,#2563eb,#d12465)",
                }}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add New
              </button>
            )}
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl shadow-md p-4">
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-sm">

              <thead className="bg-sky-50">
                <tr>
                  <th className="py-3 px-4 text-left">
                    ID
                  </th>
                  <th className="py-3 px-4 text-left">
                    Category Name
                  </th>
                  <th className="py-3 px-4 text-left">
                    Status
                  </th>
                  {(canDo("PolicyCategoryManagement", "Edit") ||
  canDo("PolicyCategoryManagement", "Delete")) && (
  <th className="py-3 px-4 text-center font-semibold">
    Actions
  </th>
)}
                </tr>
              </thead>

              <tbody>
  {loading ? (
    <tr>
      <td colSpan="4" className="text-center py-6">
        Loading...
      </td>
    </tr>
  ) : paginatedCategories.length > 0 ? (
    paginatedCategories.map((category, index) => (
      <tr
        key={category.id}
        className={`${
          index % 2 !== 0 ? "bg-[#f7f6fe]" : ""
        } border-b hover:bg-gray-50`}
      >
        <td className="py-3 px-4 text-sky-600">
          {category.id}
        </td>

        <td className="py-3 px-4">
          {category.category_name}
        </td>

        <td className="py-3 px-4">
  <span
    onClick={() =>
      statusLoading !== category.id &&
      handleStatusToggle(category)
    }
    className={`
      px-3 py-1 rounded-full text-xs font-medium
      cursor-pointer transition-all duration-200
      ${statusColors[category.status]}
      ${
        statusLoading === category.id
          ? "opacity-50 cursor-not-allowed"
          : "hover:scale-105"
      }
    `}
  >
    {statusLoading === category.id
      ? "Updating..."
      : category.status}
  </span>
</td>

{(canDo("PolicyCategoryManagement", "Edit") ||
  canDo("PolicyCategoryManagement", "Delete")) && (
        <td className="py-3 px-4 flex justify-center gap-2">
          {canDo("PolicyCategoryManagement", "Edit") && (
            <Edit2
              className="h-4 w-4 cursor-pointer hover:text-sky-600"
              onClick={() => {
                setCurrentCategory(category);
                setShowModal(true);
              }}
            />
          )}

          {canDo("PolicyCategoryManagement", "Delete") && (
            <Trash2
              className="h-4 w-4 cursor-pointer hover:text-red-600"
              onClick={() => {
                setDeleteId(category.id);
                setShowDeleteModal(true);
              }}
            />
          )}
        </td>
  )}
      </tr>
    ))
  ) : (
    <tr>
      <td colSpan="4" className="text-center py-6">
        No categories found.
      </td>
    </tr>
  )}
</tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row justify-between items-center mt-4 pt-4 border-t border-gray-100 gap-3">
            <div className="text-sm text-gray-500 flex items-center gap-2">
              <span>Rows per page:</span>
              <select
                className="border border-gray-200 bg-gray-50 rounded-lg px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb]/40 transition"
                value={itemsPerPage}
                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setPage(1); }}
              >
                {[5, 10, 15].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="flex items-center space-x-1">
              <button onClick={() => setPage(1)} disabled={page === 1} className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"><ChevronsLeft size={16} /></button>
              <button onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={page === 1} className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"><MdKeyboardArrowLeft size={16} /></button>
              {(() => {
                const start = Math.max(page - 1, 1);
                const end = Math.min(start + 2, Math.max(totalPages, 1));
                return Array.from({ length: end - start + 1 }, (_, i) => start + i).map((n) => (
                  <button key={n} onClick={() => setPage(n)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${n === page ? "text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                    style={n === page ? { background: "linear-gradient(135deg, #2563eb, #d12465)" } : undefined}
                  >{n}</button>
                ));
              })()}
              <button onClick={() => setPage((p) => Math.min(p + 1, totalPages))} disabled={page >= totalPages} className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"><MdKeyboardArrowRight size={16} /></button>
              <button onClick={() => setPage(totalPages)} disabled={page >= totalPages} className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"><ChevronsRight size={16} /></button>
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">

          <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6 relative">

            <button
              onClick={() => {
                setShowModal(false);
                setCurrentCategory(null);
              }}
              className="absolute right-4 top-4"
            >
              <X size={20} />
            </button>

            <h3 className="text-xl font-semibold mb-4">
              {currentCategory
                ? "Edit Category"
                : "Add New Category"}
            </h3>

            <form
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm font-medium mb-1">
                  Category Name
                </label>

                <input
                  type="text"
                  name="category_name"
                  defaultValue={
                    currentCategory?.category_name || ""
                  }
                  className={inputClass}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Status
                </label>

                <div className="flex gap-4">

                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="status"
                      value="active"
                      defaultChecked={
                        currentCategory?.status ===
                          "active" ||
                        !currentCategory
                      }
                      className="accent-[#8c57ff]"
                    />
                    Active
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="status"
                      value="inactive"
                      defaultChecked={
                        currentCategory?.status ===
                        "inactive"
                      }
                      className="accent-[#8c57ff]"
                    />
                    Inactive
                  </label>

                </div>
              </div>

              <div className="flex justify-end gap-3">

                <button
  type="button"
  disabled={submitting}
  onClick={() => {
    setShowModal(false);
    setCurrentCategory(null);
  }}
  className="px-4 py-2 rounded-lg border disabled:opacity-50"
>
  Cancel
</button>

                <button
  type="submit"
  disabled={submitting}
  className="px-4 py-2 rounded-lg text-white min-w-[140px] flex items-center justify-center disabled:opacity-70"
  style={{
    background: "linear-gradient(135deg, #2563eb, #d12465)",
  }}
>
  {submitting ? (
    <>
      <svg
        className="animate-spin h-4 w-4 mr-2"
        viewBox="0 0 24 24"
        fill="none"
      >
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke="white"
          strokeWidth="4"
          opacity="0.25"
        />
        <path
          d="M22 12a10 10 0 00-10-10"
          stroke="white"
          strokeWidth="4"
          strokeLinecap="round"
        />
      </svg>

      Processing...
    </>
  ) : currentCategory ? (
    "Update Category"
  ) : (
    "Create Category"
  )}
</button>

              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">

          <div className="bg-white rounded-xl p-6 max-w-sm w-full text-center">

            <h3 className="text-lg font-semibold mb-3">
              Delete Category?
            </h3>

            <p className="text-gray-600 mb-5">
              Are you sure you want to delete this
              category?
            </p>

            <div className="flex justify-center gap-4">

              <button
                onClick={() =>
                  setShowDeleteModal(false)
                }
                className="px-4 py-2 border rounded-lg"
              >
                Cancel
              </button>

              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-lg bg-red-600 text-white"
              >
                Delete
              </button>

            </div>
          </div>
        </div>
      )}
    </>
  );
}